import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RCOrganization, RCQuestionnaireObject } from '@rcenter/core';
import { environment } from '../../../../environments/environment';
import { RCServerResponse } from '../RCServiceResourceQuery';
import { buildQueryString } from '@app/react/lib/urlUtils';
import { ORGANIZATION_ID } from '@app/react/lib/localStorageKeys';
import { AuthWorker } from '../auth/AuthWorker';
import { resolveOrganizationFetchTarget } from '@app/react/components/sideMenu/organizationSwitchCommit';

const USER_ORGANIZATIONS_DEFAULT_PAGE = 1;
const USER_ORGANIZATIONS_DEFAULT_ITEMS_PER_PAGE = 30;

export interface IUserOrganizationsMeta {
	totalItems: number;
	itemsPerPage: number;
}

export interface IUserOrganizationsResponse {
	data: RCOrganization[];
	meta?: IUserOrganizationsMeta;
}

export interface IGetUserOrganizationsParams {
	page?: number;
	itemsPerPage?: number;
	search?: string;
}

export interface IActiveUserOrganizationResponse {
	organization: RCOrganization;
	totalOrganizations: number;
}

@Injectable()
export class OrganizationsService {
	constructor(private http: HttpClient) {}

	setQuestionnaire(organizationId: number, questionnaireId: number): Observable<RCServerResponse<RCOrganization>> {
		return this.http
			.put<RCServerResponse<RCOrganization>>(
				`${environment.CS_URLS.API_ROOT}/organizations/${organizationId}/questionnaire/${questionnaireId}`,
				{}
			)
			.pipe(map(response => response));
	}

	deleteQuestionnaire(organizationId: number): Observable<RCServerResponse<RCOrganization>> {
		return this.http
			.delete<RCServerResponse<RCOrganization>>(
				`${environment.CS_URLS.API_ROOT}/organizations/${organizationId}/questionnaire`,
				{}
			)
			.pipe(map(response => response));
	}

	getOrganizationQuestionnaires(organizationId: number): Observable<RCServerResponse<RCQuestionnaireObject[]>> {
		return this.http
			.get<RCServerResponse<RCQuestionnaireObject[]>>(
				`${environment.CS_URLS.API_ROOT}/organizations/${organizationId}/questionnaires`
			)
			.pipe(map(response => response));
	}

	getUserOrganizations(params: IGetUserOrganizationsParams = {}): Observable<IUserOrganizationsResponse> {
		const query = buildQueryString({
			page: params.page ?? USER_ORGANIZATIONS_DEFAULT_PAGE,
			itemsPerPage: params.itemsPerPage ?? USER_ORGANIZATIONS_DEFAULT_ITEMS_PER_PAGE,
			search: params.search,
		});

		return this.http
			.get<IUserOrganizationsResponse>(`${environment.CS_URLS.API_ROOT_V4}/user/organizations${query}`)
			.pipe(map(response => response));
	}

	getActiveUserOrganization(organizationId?: number): Observable<IActiveUserOrganizationResponse> {
		const query = buildQueryString({ organizationId });

		return this.http
			.get<IActiveUserOrganizationResponse>(`${environment.CS_URLS.API_ROOT_V4}/user/organizations/active${query}`)
			.pipe(map(response => response));
	}

	getOrganizationById(id: number) {
		return this.http
			.get<RCServerResponse<RCOrganization>>(`${environment.CS_URLS.API_ROOT_V4}/organization/${id}/backoffice`)
			.pipe(map(response => response));
	}

	/**
	 * Resolves the user's active organization, hydrates backoffice details, and stores the result on AuthWorker.
	 * Completes without emitting when a newer switch target is pinned.
	 */
	fetchActiveOrganization(preferredOrganizationId?: number): Observable<RCOrganization> {
		const organizationId = this.resolvePreferredOrganizationId(preferredOrganizationId);

		return this.getActiveUserOrganization(organizationId).pipe(
			switchMap(response => {
				if (!response?.organization) {
					throw new Error('No organization was found');
				}

				AuthWorker.setUserOrganizationsCount(response.totalOrganizations ?? 0);

				return this.getOrganizationById(response.organization.id).pipe(
					map(backofficeResponse =>
						this.normalizeCurrentOrganization(backofficeResponse?.data ?? response.organization)
					),
					catchError(() => of(this.normalizeCurrentOrganization(response.organization))),
					switchMap(organization => {
						if (!AuthWorker.canCommitOrganizationSwitch(organization.id)) {
							return EMPTY;
						}
						AuthWorker.setCurrentOrganization(organization);
						return of(organization);
					})
				);
			})
		);
	}

	private resolvePreferredOrganizationId(preferredOrganizationId?: number): number | undefined {
		const selectedOrg = localStorage.getItem(ORGANIZATION_ID);
		const parsedOrgId = selectedOrg ? Number(selectedOrg) : undefined;

		return resolveOrganizationFetchTarget({
			preferredOrganizationId,
			switchTargetId: AuthWorker.getOrganizationSwitchTargetId(),
			selectedOrganizationId: parsedOrgId,
		});
	}

	private normalizeCurrentOrganization(organization: RCOrganization): RCOrganization {
		if (!organization?.logo && organization?.mainMedia) {
			return { ...organization, logo: organization.mainMedia };
		}

		return organization;
	}

	updateOrganization(id: number, data: Partial<RCOrganization>) {
		return this.http
			.put<RCServerResponse<RCOrganization>>(`${environment.CS_URLS.API_ROOT_V4}/organization/${id}`, data)
			.pipe(map(response => response));
	}
}
